import { codiceErrore } from './codice-errore';

/**
 * Dal codice d'errore alla chiave i18n, per le schermate online.
 *
 * Regola, una sola e senza eccezioni: **a schermo va una traduzione scelta su un
 * CODICE, mai un `message`**. I messaggi degli errori sono frasi inglesi scritte
 * per i log — «Not connected to server», «Game has ended» — e mostrarle vuol dire
 * parlare in inglese a chi ha l'app in olandese, oltre che raccontare a chi
 * gioca una cosa scritta per chi programma.
 *
 * Non e' un principio astratto: `OnlinePlayersList` stampava `err.message`
 * grezzo, ed e' cosi' che «Not connected to server» finiva sotto gli occhi di
 * chi aveva solo l'app tornata dal secondo piano.
 *
 * Un codice sconosciuto non e' un buco: si ripiega sulla frase generica, che e'
 * sempre meglio di una sigla in maiuscolo o di una casella vuota.
 */
export const CHIAVI_ERRORE_ONLINE: Readonly<Record<string, string>> = {
  NOT_CONNECTED: 'online.errors.nonConnesso',
  ROOM_NOT_FOUND: 'online.errors.partitaSparita',
  NOT_IN_GAME: 'online.errors.partitaSparita',
  GAME_NOT_FOUND: 'online.errors.partitaSparita',
  AUTH_REQUIRED: 'online.errors.authRequired',
  NOT_AUTHENTICATED: 'online.errors.authRequired',
  NETWORK_ERROR: 'online.errors.nonConnesso',
  TIMEOUT: 'online.errors.nonConnesso',
  /*
   * Lo spegnimento del server. `index.ts` manda `game:error { message:
   * 'server-restart' }` a tutta la stanza — SENZA `code` — quindi
   * `multiplayer-store.onGameError` non entra in nessun ramo di rollback (quelli
   * guardano `code === MOVE_REJECTED`) e cade su `set({ error: message })`. Da qui
   * arrivava la stringa `'server-restart'`, che non era in questa mappa: chi stava
   * giocando leggeva la frase generica «qualcosa è andato storto» mentre il server
   * si stava riavviando, e la partita restava `playing` su una damiera che non
   * riceveva più niente.
   */
  'server-restart': 'online.errors.serverRestart',

  /*
   * I rifiuti della SFIDA DIRETTA.
   *
   * Prima non erano qui perche' non esistevano: il server rispondeva con la sola
   * frase inglese e `sendChallenge` la rilanciava come `Error` nudo, quindi
   * `codiceErrore` non trovava nessun `.code`. Ma soprattutto non serviva a
   * nessuno, perche' chi chiamava inghiottiva l'esito con un `log.error`: ogni
   * rifiuto era un clic che non faceva niente e non diceva niente.
   *
   * Quasi tutti sono riparabili da chi li legge — cancella la sfida che hai in
   * volo, esci dal torneo, aspetta che finisca la sua partita — e una frase che
   * lo dice vale piu' di dieci che si scusano.
   */
  CHALLENGE_RATE_LIMITED: 'online.errors.sfidaTroppeRichieste',
  TARGET_OFFLINE: 'online.errors.avversarioOffline',
  TARGET_BUSY: 'online.errors.avversarioInPartita',
  IN_COMPETITION: 'online.errors.seiInGara',
  TARGET_IN_COMPETITION: 'online.errors.avversarioInGara',
  TOO_MANY_POSITIONS: 'online.errors.troppePosizioni',
  INVALID_SETTINGS: 'online.errors.impostazioniNonValide',
  REGISTERED_ONLY: 'online.errors.soloIscritti',
  CHALLENGE_PENDING: 'online.errors.sfidaGiaInVolo',
  CHALLENGE_DUPLICATE: 'online.errors.sfidaGiaMandata',
  /*
   * Ha una frase SUA e non riusa `TARGET_BUSY`: quello dice «e' in partita», che
   * di un occupato sarebbe falso — e soprattutto fa aspettare la fine di una
   * partita che non c'e'.
   */
  TARGET_DO_NOT_DISTURB: 'online.errors.avversarioNonDisturbare',
  /*
   * Tre codici restano di proposito sulla frase generica: `SELF_CHALLENGE`,
   * `CANNOT_CHALLENGE` (un bot che sfida) e `GUEST_CANNOT_CHALLENGE`. Nessuna
   * schermata dell'app puo' produrli — non si sfida se stessi, i bot non usano
   * questo modulo, e a un ospite la Sfida diretta mostra l'invito a registrarsi
   * al posto del modulo. Tradurre un rifiuto che solo un `challenge:send`
   * costruito a mano puo' far scattare sarebbe otto frasi per nessuno.
   */
};

/** La frase da dire quando il codice non lo conosciamo (o non c'e'). */
export const CHIAVE_ERRORE_GENERICA = 'online.errors.generico';

/** Il prefisso delle chiavi di questo dominio: `online.errors.qualcosa`. */
const PREFISSO_CHIAVI = 'online.errors.';
const PREFISSO_CHIAVI_TORNEO = 'tournament.errors.';

/**
 * La chiave i18n per un codice gia' estratto.
 *
 * Se il valore E' GIA' UNA CHIAVE di questo dominio, passa intatto: alcuni punti
 * dello store scrivono direttamente la chiave, perche' il codice del server la' non
 * arriva (`rollbackOptimisticMove` ne riceve solo la frase) o perche' l'errore nasce
 * sul client (`online.errors.alreadyInGame`). Senza questo passaggio quei valori non
 * si trovavano nella mappa e ripiegavano sulla frase generica: una traduzione
 * precisa buttata via per un giro di traduzione in piu'.
 */
export function chiaveErroreOnline(codice: string | undefined | null): string {
  if (!codice) return CHIAVE_ERRORE_GENERICA;
  if (codice.startsWith(PREFISSO_CHIAVI)) return codice;
  /*
   * Anche le chiavi dei TORNEI passano intatte.
   *
   * Lo store dei tornei traduce i codici del server in chiavi `tournament.errors.*`
   * — esistono, sono buone e sono tradotte in tutte e otto le lingue («Non e' il
   * tuo turno.», «Il server ha rifiutato la tua mossa: la scacchiera e' tornata
   * com'era.») — ma questa funzione passava solo il prefisso `online.errors.` e
   * tutto il resto finiva nel ripiego generico. Risultato: in una partita di
   * torneo OGNI errore si leggeva «Qualcosa non ha funzionato», con la frase
   * giusta gia' pronta e buttata via.
   */
  if (codice.startsWith(PREFISSO_CHIAVI_TORNEO)) return codice;
  return CHIAVI_ERRORE_ONLINE[codice] ?? CHIAVE_ERRORE_GENERICA;
}

/** La chiave i18n per un errore, qualunque forma abbia. Vedi `codiceErrore`. */
export function chiaveErroreOnlinePer(err: unknown): string {
  return chiaveErroreOnline(codiceErrore(err));
}
